/**
 * Pure draft state + validation for the "add your employment details" dialog
 * an employer sees when `payroll.linkSelfAsEmployee` finds no employee record
 * for their own email (`my_pay_self_link_dialog.tsx`).
 *
 * Mirrors the backend's `selfLinkProfileInput` + `validateEmployeeProfile`
 * (`apps/api/src/trpc/routers/payroll.ts`,
 * `apps/api/src/trpc/routers/payroll_employee_validation.ts`): pay basis, the
 * matching rate (hourly) or salary (salaried), and the start date are all
 * required, so a bad value is caught before a round trip. Money is always
 * minor units (cents) per docs/money-rules.md.
 */
import type { PayBasis } from '../employees/employee_form_validation';

export interface SelfLinkProfileDraft {
  pay_basis: PayBasis;
  /** Hourly rate (hourly) or monthly salary (salaried), in minor units. */
  amount_cents: number;
  /** ISO `YYYY-MM-DD`, or `''` until picked. */
  employment_start_date: string;
  job_title: string;
}

/** A blank draft, mirroring the create form's own default basis (hourly). */
export function emptySelfLinkProfileDraft(): SelfLinkProfileDraft {
  return {
    pay_basis: 'hourly',
    amount_cents: 0,
    employment_start_date: '',
    job_title: '',
  };
}

export type SelfLinkProfileFieldError =
  | 'required'
  | 'negative_amount'
  | 'salary_required'
  | 'rate_required';

export interface SelfLinkProfileErrors {
  amount?: SelfLinkProfileFieldError;
  employment_start_date?: SelfLinkProfileFieldError;
}

/** Field errors for a draft; an empty object means it may be submitted. */
export function validateSelfLinkProfile(
  draft: SelfLinkProfileDraft,
): SelfLinkProfileErrors {
  const errors: SelfLinkProfileErrors = {};
  if (draft.amount_cents < 0) {
    errors.amount = 'negative_amount';
  } else if (draft.amount_cents === 0) {
    errors.amount =
      draft.pay_basis === 'salaried' ? 'salary_required' : 'rate_required';
  }
  if (draft.employment_start_date.trim() === '') {
    errors.employment_start_date = 'required';
  }
  return errors;
}

/** The `profile` wire shape `payroll.linkSelfAsEmployee` accepts. */
export interface SelfLinkProfileInput {
  pay_basis: PayBasis;
  hourly_rate_cents: number;
  monthly_salary_cents: number | null;
  employment_start_date: string;
  job_title: string | null;
}

/** Build the wire payload from a draft that passed {@link validateSelfLinkProfile}. */
export function buildSelfLinkProfileInput(
  draft: SelfLinkProfileDraft,
): SelfLinkProfileInput {
  const hourly = draft.pay_basis === 'hourly';
  const job_title = draft.job_title.trim();
  return {
    pay_basis: draft.pay_basis,
    hourly_rate_cents: hourly ? draft.amount_cents : 0,
    monthly_salary_cents: hourly ? null : draft.amount_cents,
    employment_start_date: draft.employment_start_date,
    job_title: job_title === '' ? null : job_title,
  };
}
